/**
 * Standardized column width categories for consistent sizing across all table types
 * 
 * This file provides a centralized location for all column width standards,
 * ensuring consistency across project task tables, entity tables, and any future table types.
 */

/**
 * Standard width categories with consistent naming and sizing
 */
export const STANDARD_COLUMN_WIDTHS = {
  // Very narrow - icons, small numbers, boolean indicators (35-50px)
  VERY_NARROW: { width: "50px", minWidth: "35px" },
  // Narrow - short text, priorities, small dropdowns (80-120px)
  NARROW: { width: "120px", minWidth: "80px" },
  // Medium - standard text fields, dates, status fields (120-160px)
  MEDIUM: { width: "160px", minWidth: "120px" },
  // Wide - longer text fields, assignees, groups, descriptions (140-200px)
  WIDE: { width: "200px", minWidth: "140px" },
  // Very wide - names, main content fields (200-280px)
  VERY_WIDE: { width: "280px", minWidth: "200px" },
} as const;

/**
 * Field type to width category mapping for custom fields
 * This provides consistent widths based on the expected content type
 */
export const FIELD_TYPE_WIDTHS = {
  STRING: STANDARD_COLUMN_WIDTHS.WIDE,
  TEXT: STANDARD_COLUMN_WIDTHS.WIDE,
  RICH_TEXT: STANDARD_COLUMN_WIDTHS.WIDE,
  NUMBER: STANDARD_COLUMN_WIDTHS.NARROW,
  BOOLEAN: STANDARD_COLUMN_WIDTHS.NARROW,
  DATE: STANDARD_COLUMN_WIDTHS.MEDIUM,
  DATETIME: STANDARD_COLUMN_WIDTHS.MEDIUM,
  DROPDOWN: STANDARD_COLUMN_WIDTHS.MEDIUM,
  FILE: STANDARD_COLUMN_WIDTHS.WIDE,
  USER: STANDARD_COLUMN_WIDTHS.MEDIUM,
  EMAIL: STANDARD_COLUMN_WIDTHS.WIDE,
  URL: STANDARD_COLUMN_WIDTHS.WIDE,
  PHONE: STANDARD_COLUMN_WIDTHS.MEDIUM,
} as const;

/**
 * Helper function to get width settings for a field type
 * @param fieldType - The field type to get width for
 * @returns Width and minWidth settings
 */
export function getFieldTypeWidth(fieldType: string): { width: string; minWidth: string } {
  const upperFieldType = fieldType.toUpperCase() as keyof typeof FIELD_TYPE_WIDTHS;
  return FIELD_TYPE_WIDTHS[upperFieldType] || STANDARD_COLUMN_WIDTHS.MEDIUM;
}

/**
 * Special column widths for specific use cases
 */
export const SPECIAL_COLUMN_WIDTHS = {
  // Task-specific icon columns (subtasks, comments, dependencies)
  TASK_ICON: { width: "35px", minWidth: "35px" },
  // Table selection checkbox column
  SELECTION: { width: "40px", minWidth: "40px" },
  // View/actions column
  ACTIONS: { width: "60px", minWidth: "60px" },
} as const;

/**
 * Column width presets for common column types across different modules
 */
export const COLUMN_PRESETS = {
  // Name columns (primary content)
  NAME: STANDARD_COLUMN_WIDTHS.VERY_WIDE,
  // Status and priority columns
  STATUS: STANDARD_COLUMN_WIDTHS.MEDIUM,
  PRIORITY: STANDARD_COLUMN_WIDTHS.NARROW,
  // Assignment and ownership columns
  ASSIGNEES: STANDARD_COLUMN_WIDTHS.WIDE,
  OWNER: STANDARD_COLUMN_WIDTHS.MEDIUM,
  // Date columns
  DUE_DATE: STANDARD_COLUMN_WIDTHS.MEDIUM,
  CREATED_DATE: STANDARD_COLUMN_WIDTHS.MEDIUM,
  UPDATED_DATE: STANDARD_COLUMN_WIDTHS.MEDIUM,
  // Contact information
  EMAIL: STANDARD_COLUMN_WIDTHS.WIDE,
  PHONE: STANDARD_COLUMN_WIDTHS.MEDIUM,
  WEBSITE: STANDARD_COLUMN_WIDTHS.WIDE,
  ADDRESS: STANDARD_COLUMN_WIDTHS.WIDE,
  // Numeric columns
  COUNT: STANDARD_COLUMN_WIDTHS.NARROW,
  STORY_POINTS: STANDARD_COLUMN_WIDTHS.VERY_NARROW,
  HOURS: STANDARD_COLUMN_WIDTHS.NARROW,
  // Description and content
  DESCRIPTION: STANDARD_COLUMN_WIDTHS.WIDE,
  NOTES: STANDARD_COLUMN_WIDTHS.WIDE,
  // Groups and categories
  GROUP: STANDARD_COLUMN_WIDTHS.WIDE,
  CATEGORY: STANDARD_COLUMN_WIDTHS.MEDIUM,
} as const;

export type ColumnWidthCategory = keyof typeof STANDARD_COLUMN_WIDTHS;
export type ColumnPreset = keyof typeof COLUMN_PRESETS;